import { useState, useEffect } from 'react';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Trash2, Edit, FileText, ArrowUpRight, ArrowDownRight, Calendar } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { formatDateDisplay } from '@/utils/formatters';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${API_URL}/api`;

export default function BankAccounts() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState(null);
  const { isAdmin } = useAuth();
  
  // Estado para o extrato
  const [extratoDialogOpen, setExtratoDialogOpen] = useState(false);
  const [extratoAccount, setExtratoAccount] = useState(null);
  const [extratoTransactions, setExtratoTransactions] = useState([]);
  const [extratoLoading, setExtratoLoading] = useState(false);
  const [extratoFilter, setExtratoFilter] = useState('all'); // 'all', 'entrada', 'saida'
  const [extratoPeriodo, setExtratoPeriodo] = useState('all'); // 'all', '7', '30', '90', 'custom'
  const [extratoDataInicio, setExtratoDataInicio] = useState('');
  const [extratoDataFim, setExtratoDataFim] = useState('');

  const [formData, setFormData] = useState({
    nome: '',
    banco: '',
    saldo: '',
    conta_cartao: false,
    limite_cheque_especial: ''
  });

  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      const response = await axios.get(`${API}/bank-accounts`);
      setAccounts(response.data);
    } catch (error) {
      toast.error('Erro ao carregar contas bancárias');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        saldo: parseFloat(formData.saldo) || 0,
        limite_cheque_especial: parseFloat(formData.limite_cheque_especial) || 0
      };

      if (editingAccount) {
        await axios.put(`${API}/bank-accounts/${editingAccount.id}`, payload);
        toast.success('Conta atualizada com sucesso!');
      } else {
        await axios.post(`${API}/bank-accounts`, payload);
        toast.success('Conta adicionada com sucesso!');
      }

      setDialogOpen(false);
      setEditingAccount(null);
      fetchAccounts();
      setFormData({ nome: '', banco: '', saldo: '', conta_cartao: false, limite_cheque_especial: '' });
    } catch (error) {
      toast.error('Erro ao salvar conta');
    }
  };

  const handleEdit = (account) => {
    setEditingAccount(account);
    setFormData({
      nome: account.nome,
      banco: account.banco,
      saldo: account.saldo.toString(),
      conta_cartao: account.conta_cartao || false,
      limite_cheque_especial: (account.limite_cheque_especial || 0).toString()
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Deseja realmente excluir esta conta?')) return;
    try {
      await axios.delete(`${API}/bank-accounts/${id}`);
      toast.success('Conta excluída!');
      fetchAccounts();
    } catch (error) {
      toast.error('Erro ao excluir conta');
    }
  };

  // Função para abrir o extrato da conta
  const handleOpenExtrato = async (account) => {
    setExtratoAccount(account);
    setExtratoDialogOpen(true);
    setExtratoLoading(true);
    setExtratoFilter('all');
    setExtratoPeriodo('all');
    setExtratoDataInicio('');
    setExtratoDataFim('');
    
    try {
      const response = await axios.get(`${API}/bank-accounts/${account.id}/transactions`);
      setExtratoTransactions(response.data);
    } catch (error) {
      toast.error('Erro ao carregar extrato');
      setExtratoTransactions([]);
    } finally {
      setExtratoLoading(false);
    }
  };

  // Filtrar transações do extrato por tipo e período
  const filteredExtratoTransactions = extratoTransactions.filter(t => {
    // Filtro por tipo
    if (extratoFilter === 'entrada' && (t.tipo === 'despesa' || t.operacao === 'saida')) return false;
    if (extratoFilter === 'saida' && t.tipo !== 'despesa' && t.operacao !== 'saida') return false;
    
    // Filtro por período
    if (extratoPeriodo !== 'all' && t.data) {
      const transDate = new Date(t.data);
      const today = new Date();
      today.setHours(23, 59, 59, 999);
      
      if (extratoPeriodo === 'custom') {
        if (extratoDataInicio && transDate < new Date(extratoDataInicio)) return false;
        if (extratoDataFim) {
          const fimDate = new Date(extratoDataFim);
          fimDate.setHours(23, 59, 59, 999);
          if (transDate > fimDate) return false;
        }
      } else {
        const dias = parseInt(extratoPeriodo);
        const startDate = new Date();
        startDate.setDate(startDate.getDate() - dias);
        startDate.setHours(0, 0, 0, 0);
        if (transDate < startDate) return false;
      }
    }
    
    return true;
  });

  // Calcular totais do extrato (baseado nas transações filtradas)
  // Incluir valor_adicional (juros/tarifa) nos cálculos
  const extratoTotais = {
    entradas: filteredExtratoTransactions
      .filter(t => t.tipo !== 'despesa' && t.operacao !== 'saida')
      .reduce((acc, t) => acc + t.valor, 0),
    saidas: filteredExtratoTransactions
      .filter(t => t.tipo === 'despesa' || t.operacao === 'saida')
      .reduce((acc, t) => acc + t.valor + (t.valor_adicional || 0), 0),
    juros: filteredExtratoTransactions
      .filter(t => t.tipo_adicional === 'juros')
      .reduce((acc, t) => acc + (t.valor_adicional || 0), 0),
    tarifas: filteredExtratoTransactions
      .filter(t => t.tipo_adicional === 'tarifa')
      .reduce((acc, t) => acc + (t.valor_adicional || 0), 0)
  };

  const totalBalance = accounts.reduce((acc, a) => acc + a.saldo, 0);
  const totalLimite = accounts.reduce((acc, a) => acc + (a.limite_cheque_especial || 0), 0);
  const totalDisponivel = totalBalance + totalLimite;

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-muted-foreground">Carregando...</p>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-4xl font-bold font-manrope tracking-tight">Contas Bancárias</h1>
            <p className="text-muted-foreground mt-1">Gerencie suas contas e saldos</p>
          </div>
          {isAdmin() && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) {
                setEditingAccount(null);
                setFormData({ nome: '', banco: '', saldo: '', conta_cartao: false, limite_cheque_especial: '' });
              }
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-account-button" className="shadow-[0_0_10px_rgba(52,211,153,0.2)]">
                  <Plus className="h-4 w-4 mr-2" />
                  Nova Conta
                </Button>
              </DialogTrigger>
              <DialogContent className="w-full max-w-[95vw] sm:max-w-md max-h-[85vh] overflow-y-auto overflow-x-hidden pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
                <DialogHeader>
                  <DialogTitle className="font-manrope">
                    {editingAccount ? 'Editar Conta' : 'Nova Conta Bancária'}
                  </DialogTitle>
                  <DialogDescription>
                    {editingAccount ? 'Atualize os dados da conta' : 'Adicione uma nova conta bancária'}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="nome">Nome da Conta</Label>
                    <Input
                      id="nome"
                      placeholder="Ex: Conta Corrente Principal"
                      value={formData.nome}
                      onChange={(e) => setFormData({ ...formData, nome: e.target.value })}
                      required
                      data-testid="nome-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="banco">Banco</Label>
                    <Input
                      id="banco"
                      placeholder="Ex: Banco do Brasil"
                      value={formData.banco}
                      onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                      required
                      data-testid="banco-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="saldo">Saldo</Label>
                    <Input
                      id="saldo"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.saldo}
                      onChange={(e) => setFormData({ ...formData, saldo: e.target.value })}
                      required
                      data-testid="saldo-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="limite_cheque_especial">Limite do Cheque Especial</Label>
                    <Input
                      id="limite_cheque_especial"
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.limite_cheque_especial}
                      onChange={(e) => setFormData({ ...formData, limite_cheque_especial: e.target.value })}
                      data-testid="limite-cheque-input"
                    />
                    <p className="text-xs text-muted-foreground">Valor disponível além do saldo (opcional)</p>
                  </div>

                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="conta_cartao"
                      checked={formData.conta_cartao}
                      onChange={(e) => setFormData({ ...formData, conta_cartao: e.target.checked })}
                      className="h-4 w-4 rounded border-border"
                      data-testid="conta-cartao-checkbox"
                    />
                    <Label htmlFor="conta_cartao" className="text-sm font-normal cursor-pointer">
                      Conta para recebimento de cartões (Débito/Crédito)
                    </Label>
                  </div>

                  <Button type="submit" className="w-full" data-testid="submit-account">
                    {editingAccount ? 'Atualizar' : 'Adicionar'}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Total Balance */}
        <Card className="border-border bg-gradient-to-br from-primary/10 to-transparent">
          <CardHeader>
            <CardTitle className="text-sm font-medium text-muted-foreground">Resumo Financeiro</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <p className="text-sm text-muted-foreground mb-1">Saldo Total</p>
                <div className={`text-3xl font-bold font-mono ${totalBalance >= 0 ? 'text-green-500' : 'text-red-500'}`} data-testid="total-balance">
                  R$ {totalBalance.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Limite Total</p>
                <div className="text-3xl font-bold font-mono text-blue-500" data-testid="total-limite">
                  R$ {totalLimite.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
              <div>
                <p className="text-sm text-muted-foreground mb-1">Total Disponível</p>
                <div className={`text-3xl font-bold font-mono ${totalDisponivel >= 0 ? 'text-green-500' : 'text-red-500'}`} data-testid="total-disponivel">
                  R$ {totalDisponivel.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
              </div>
            </div>
            <p className="text-sm text-muted-foreground mt-4">{accounts.length} contas cadastradas</p>
          </CardContent>
        </Card>

        {/* Accounts Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {accounts.map(account => (
            <Card key={account.id} className="border-border hover:border-primary/50 transition-colors" data-testid={`account-${account.id}`}>
              <CardHeader>
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <CardTitle className="font-manrope text-lg">{account.nome}</CardTitle>
                    <p className="text-sm text-muted-foreground mt-1">{account.banco}</p>
                  </div>
                  {isAdmin() && (
                    <div className="flex gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleEdit(account)}
                        data-testid={`edit-account-${account.id}`}
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(account.id)}
                        data-testid={`delete-account-${account.id}`}
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <div className={`text-2xl font-bold font-mono ${account.saldo >= 0 ? 'text-green-500' : 'text-red-500'}`}>
                  R$ {account.saldo.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                {(account.limite_cheque_especial || 0) > 0 && (
                  <div className="mt-2 text-sm">
                    <span className="text-muted-foreground">Limite: </span>
                    <span className="text-blue-500 font-mono">
                      R$ {(account.limite_cheque_especial || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                    <span className="text-muted-foreground mx-2">|</span>
                    <span className="text-muted-foreground">Disponível: </span>
                    <span className="text-green-500 font-mono">
                      R$ {(account.saldo + (account.limite_cheque_especial || 0)).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </span>
                  </div>
                )}
                {account.conta_cartao && (
                  <span className="inline-block mt-2 px-2 py-1 text-xs bg-primary/20 text-primary rounded">
                    Conta de Cartões
                  </span>
                )}
                
                {/* Botão Ver Extrato */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full mt-4"
                  onClick={() => handleOpenExtrato(account)}
                  data-testid={`extrato-account-${account.id}`}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Ver Extrato
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Modal de Extrato */}
        <Dialog open={extratoDialogOpen} onOpenChange={setExtratoDialogOpen}>
          <DialogContent className="w-full max-w-[95vw] sm:max-w-4xl max-h-[85vh] overflow-hidden flex flex-col pb-6 mb-6 sm:mb-0 rounded-2xl sm:rounded-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Extrato - {extratoAccount?.nome}
              </DialogTitle>
              <DialogDescription>
                {extratoAccount?.banco} • Saldo atual: R$ {extratoAccount?.saldo?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </DialogDescription>
            </DialogHeader>
            
            {extratoLoading ? (
              <div className="flex items-center justify-center py-12">
                <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="flex-1 overflow-hidden flex flex-col">
                {/* Resumo */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Entradas</p>
                    <p className="text-lg font-bold text-green-500 font-mono">
                      R$ {extratoTotais.entradas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Saídas</p>
                    <p className="text-lg font-bold text-red-500 font-mono">
                      R$ {extratoTotais.saidas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Juros</p>
                    <p className="text-lg font-bold text-orange-500 font-mono">
                      R$ {extratoTotais.juros.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Tarifas</p>
                    <p className="text-lg font-bold text-orange-500 font-mono">
                      R$ {extratoTotais.tarifas.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </p>
                  </div>
                  <div className="p-3 bg-muted/30 rounded-lg">
                    <p className="text-xs text-muted-foreground">Transações</p>
                    <p className="text-lg font-bold font-mono">{filteredExtratoTransactions.length}</p>
                  </div>
                </div>

                {/* Filtros */}
                <div className="flex gap-2 mb-4">
                  <Button
                    variant={extratoFilter === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoFilter('all')}
                  >
                    Todos
                  </Button>
                  <Button
                    variant={extratoFilter === 'entrada' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoFilter('entrada')}
                    className={extratoFilter === 'entrada' ? 'bg-green-600 hover:bg-green-700' : ''}
                  >
                    <ArrowUpRight className="h-4 w-4 mr-1" />
                    Entradas
                  </Button>
                  <Button
                    variant={extratoFilter === 'saida' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoFilter('saida')}
                    className={extratoFilter === 'saida' ? 'bg-red-600 hover:bg-red-700' : ''}
                  >
                    <ArrowDownRight className="h-4 w-4 mr-1" />
                    Saídas
                  </Button>
                </div>

                {/* Filtro por Período */}
                <div className="flex flex-wrap gap-2 mb-4 items-center">
                  <span className="text-sm text-muted-foreground mr-2">Período:</span>
                  <Button
                    variant={extratoPeriodo === 'all' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoPeriodo('all')}
                  >
                    Todos
                  </Button>
                  <Button
                    variant={extratoPeriodo === '7' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoPeriodo('7')}
                  >
                    7 dias
                  </Button>
                  <Button
                    variant={extratoPeriodo === '30' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoPeriodo('30')}
                  >
                    30 dias
                  </Button>
                  <Button
                    variant={extratoPeriodo === '90' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoPeriodo('90')}
                  >
                    90 dias
                  </Button>
                  <Button
                    variant={extratoPeriodo === 'custom' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setExtratoPeriodo('custom')}
                  >
                    <Calendar className="h-4 w-4 mr-1" />
                    Personalizado
                  </Button>
                </div>

                {/* Filtro Personalizado de Datas */}
                {extratoPeriodo === 'custom' && (
                  <div className="flex gap-4 mb-4 p-3 bg-muted/30 rounded-lg">
                    <div className="flex-1">
                      <Label className="text-xs">Data Início</Label>
                      <Input
                        type="date"
                        value={extratoDataInicio}
                        onChange={(e) => setExtratoDataInicio(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                    <div className="flex-1">
                      <Label className="text-xs">Data Fim</Label>
                      <Input
                        type="date"
                        value={extratoDataFim}
                        onChange={(e) => setExtratoDataFim(e.target.value)}
                        className="mt-1"
                      />
                    </div>
                  </div>
                )}

                {/* Lista de Transações */}
                <div className="flex-1 overflow-auto border rounded-lg">
                  {filteredExtratoTransactions.length > 0 ? (
                    <table className="w-full">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground">Data</th>
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground">Descrição</th>
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground">Tipo</th>
                          <th className="text-left p-3 text-xs font-medium text-muted-foreground">Categoria</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Valor</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Juros/Tarifa</th>
                          <th className="text-right p-3 text-xs font-medium text-muted-foreground">Total</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {filteredExtratoTransactions.map((t, idx) => {
                          const isEntrada = t.tipo !== 'despesa' && t.operacao !== 'saida';
                          const valorTotal = t.valor + (t.valor_adicional || 0);
                          return (
                            <tr key={t.id || idx} className="hover:bg-muted/30">
                              <td className="p-3 text-sm">
                                <div className="flex items-center gap-2">
                                  <Calendar className="h-3 w-3 text-muted-foreground" />
                                  {formatDateDisplay(t.data)}
                                </div>
                              </td>
                              <td className="p-3 text-sm">{t.descricao || '-'}</td>
                              <td className="p-3 text-sm">
                                <span className={`px-2 py-1 rounded text-xs ${
                                  isEntrada ? 'bg-green-500/20 text-green-500' : 'bg-red-500/20 text-red-500'
                                }`}>
                                  {isEntrada ? 'Entrada' : 'Saída'}
                                </span>
                              </td>
                              <td className="p-3 text-sm text-muted-foreground">{t.categoria || '-'}</td>
                              <td className={`p-3 text-sm font-mono text-right ${isEntrada ? 'text-green-500' : 'text-red-500'}`}>
                                {isEntrada ? '+' : '-'} R$ {t.valor?.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                              <td className="p-3 text-sm font-mono text-right text-orange-500">
                                {t.valor_adicional ? (
                                  <>
                                    {t.tipo_adicional === 'juros' ? 'J: ' : 'T: '}
                                    R$ {t.valor_adicional.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                  </>
                                ) : '-'}
                              </td>
                              <td className={`p-3 text-sm font-mono text-right font-bold ${isEntrada ? 'text-green-500' : 'text-red-500'}`}>
                                {isEntrada ? '+' : '-'} R$ {valorTotal.toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div className="flex items-center justify-center py-12 text-muted-foreground">
                      Nenhuma transação encontrada
                    </div>
                  )}
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {accounts.length === 0 && (
          <Card className="border-border">
            <CardContent className="py-16 text-center">
              <p className="text-muted-foreground mb-4">Nenhuma conta bancária cadastrada</p>
              {isAdmin() && (
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Adicionar Primeira Conta
                </Button>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </Layout>
  );
}